<template>
  <!-- h-100 or vh-100 ensures the wrapper stays pinned inside its parent view -->
  <div v-if="selectedDevice" class="chat-view-wrapper d-flex flex-column p-3 mx-auto w-100 h-100 overflow-hidden">
    
    <!-- Chat history area: flex-grow expands to fill space, overflow-y handles scrolling -->
    <div
      ref="chatContainer"
      class="chat-container flex-grow-1 border rounded p-3 overflow-y-auto mb-3"
    >
      <div
        v-for="(message, index) in messages"
        :key="index"
        :class="getChatBubbleClass(message)"
      >
        <small class="fw-light fst-italic">[{{ message.time }}]</small> 
        <div>{{ message.text }}</div>
      </div>
    </div>

    <!-- Input group stays structurally pinned at the absolute bottom of the container -->
    <div class="input-group">
      <input
        v-model="newMessage"
        class="form-control"
        type="text"
        placeholder="Type a message"
        @keyup.enter="handleSendMessage"
      />
      <button class="btn btn-primary" @click="handleSendMessage">Send</button>
    </div>
  </div>
  <div v-else class="d-flex flex-column text-center">
    <span class="text-muted">
      Please select a device to chat with.
      {{ availableDevices.length ? `(${availableDevices.length} available)` : '' }}
    </span>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { isPlainObject } from 'lodash-es';
import { ApiEndpoint } from '@/utils/config';
import { apiFetch } from '@/utils/api';

const props = defineProps({
  selectedDevice: {
    type: String,
    default: ''
  },
  availableDevices: { type: Array, default: [] },
  messages: {
    type: Array,
    required: true,
    validator(messages) {
      const tags = ['time', 'sender', 'text'];
      return messages.every(
        msg => isPlainObject(msg) && tags.every(tag => Object.hasOwn(msg, tag))
      );
    }
  }
});

const emit = defineEmits(['sentText']);

// Reactive State Variables
const newMessage = ref('');
const chatContainer = ref(null); // Template Ref mapping directly to the DOM node

// Logic for Dynamic Chat Bubble CSS Classes
const getChatBubbleClass = (msg) => {
  let chatBubbleClass = 'chat-bubble ';
  const sender = msg.sender?.toLowerCase() || '';
  const state = msg.state?.toUpperCase() || '';

  if (['central', 'dispatch'].includes(sender)) {
    chatBubbleClass += 'central';
    if (state.includes('FAIL')) {
      chatBubbleClass += '-failed';
    } else if (state.includes('RECEIVED')) {
      chatBubbleClass += '-success';
    }
  } else {
    chatBubbleClass += 'remote';
  }
  return chatBubbleClass;
};

// Scroll to bottom of chat area securely using nextTick
const updateScroller = () => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    }
  });
};

const sendUrl = ApiEndpoint.DEMO + `/send`;

const handleSendMessage = async () => {
  if (!props.selectedDevice || newMessage.value.trim() === '') return;
  try {
    const result = await apiFetch(sendUrl, {
      method: 'POST',
      body: {
        deviceId: props.selectedDevice, 
        payload: {
          name: 'textMt',
          direction: 'DOWNLINK',
          value: {
            text: newMessage.value,
          }
        },
      },
    });
    console.debug(`Sent ${JSON.stringify(result)}`);
    emit('sentText', newMessage.value);
  } catch (err) {
    console.error("Failed to send text message:", err);
    confirm(`Error sending message: ${err.message}`);
  }
  newMessage.value = '';
};

// Watch for deep changes inside messages array to auto-scroll down
watch(
  () => props.messages,
  () => {
    updateScroller();
  },
  { deep: true, immediate: true }
);
</script>

<style scoped>
/* Main viewport container layout control */
.chat-view-wrapper {
  max-width: 600px;
  height: 80vh; /* Keeps entire layout restricted to 80% viewport height. Switch to 100vh if full screen is preferred */
}

.device-header {
  font-size: 0.9rem;
}

.chat-container {
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column; /* Aligns chat message items along vertical block flow axis */
}

.chat-bubble {
  padding: 10px 15px;
  margin-bottom: 10px;
  border-radius: 20px;
  max-width: 75%;
  word-wrap: break-word;
}

.chat-bubble.central {
  background-color: #e7e7d1;
  align-self: flex-end;
  text-align: right;
  margin-left: auto;
}

.chat-bubble.central-success {
  background-color: #d1e7dd;
  align-self: flex-end;
  text-align: right;
  margin-left: auto;
}

.chat-bubble.central-failed {
  background-color: #611518;
  color: white;
  align-self: flex-end;
  text-align: right;
  margin-left: auto;
}

.chat-bubble.remote {
  background-color: #e2e3e5;
  align-self: flex-start;
  text-align: left;
  margin-right: auto;
}
</style>
